/**
 * Wall-clock datetimes from the Scrips backend.
 *
 * The services convert an instant into the CALLER's zone before serialising it
 * (`DateTimeHelper.ConvertTimeFromUtc(..., UserTimeZone)` in .NET), so the
 * digits in the string are already the clinic-local wall clock. The kind of the
 * converted `DateTime` decides whether a suffix is printed: NodaTime's
 * `ToDateTimeUnspecified()` prints none (`"2026-11-10T11:00:00"`), but a value
 * that skipped the conversion, or a projection that re-stamps it, can print `Z`.
 *
 * Neither form is safe to hand to `new Date(iso)`:
 *   - no suffix -> parsed as the VIEWER's local time; any UTC getter then
 *     subtracts the viewer's offset (11:00 reads as 7:00 in Dubai, PROD-2527);
 *   - `Z` suffix -> parsed as UTC; any local getter then ADDS the offset.
 *
 * So these strings are parsed TEXTUALLY — date + time digits only — and any
 * suffix (`Z`, `+04:00`) is ignored on purpose. The result is a local `Date`
 * whose LOCAL getters return the wall clock that was sent, on every machine.
 */

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

/** Textual local parse — see the module header. `null` when it isn't a datetime. */
export function parseWallClock(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = WALL_CLOCK.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    s ? Number(s) : 0,
    0,
  );
  return Number.isNaN(date.getTime()) ? null : date;
}
